/**
 * Pojedynczy artykuł bloga — /:slug/
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useLocation } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowUp, Clock, Calendar, ChevronRight } from 'lucide-react';
import {
  getPostBySlug, getRelatedPosts, formatDate,
  type Post, type PostSeo,
} from '@/data/blog';
import { Footer } from '@/components/sections/Footer';
import { useSeo } from '@/lib/useSeo';
import { articleUrl } from '@/lib/articleUrl';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { parseContentBlocks } from '@/lib/blogContentBlocks';
import { BlogContentBlocks } from '@/components/blog/BlogContentBlocks';
import { ARTICLE_PUBLISHER } from '@/lib/globalFloristSchema';
import DOMPurify from 'dompurify';
import { serializeCmsJson } from '@workspace/url-source/cms';

/* ── Tokens ─────────────────────────────────────────────── */
const GOLD   = '#e05590';
const DARK   = '#2a1f14';
const MUTED  = 'rgba(42,31,20,0.50)';
const LINE   = 'rgba(210,60,120,0.18)';
const CREAM  = '#fff';
const CREAM2 = '#fff';
const CREAM3 = '#fff';

const ease = [0.22, 1, 0.36, 1] as const;
const fadeUp = (delay = 0) => ({
  initial:     { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport:    { once: true, margin: '-40px' },
  transition:  { duration: 0.55, delay, ease },
});

/* ── Post image ─────────────────────────────────────────── */
function PostImage({ src, alt, style }: { src?: string; alt?: string; style?: React.CSSProperties }) {
  if (!src) {
    return (
      <div style={{ background: '#fff', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}>
        <span style={{ fontFamily: 'DM Sans', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: `${GOLD}55` }}>Bukietowa</span>
      </div>
    );
  }
  return <img src={src} alt={alt ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', ...style }} />;
}

/* ── Related card ───────────────────────────────────────── */
function RelatedCard({ post }: { post: Post }) {
  return (
    <Link href={articleUrl(post.slug)} style={{ textDecoration: 'none', display: 'block' }}>
      <motion.article {...fadeUp()} style={{ cursor: 'pointer' }}>
        <div style={{ aspectRatio: '3/2', overflow: 'hidden', background: '#fff', marginBottom: 12 }}>
          <PostImage src={post.image} alt={post.imageAlt} />
        </div>
        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD, margin: '0 0 7px' }}>
          {post.category}
        </p>
        <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(16px,1.6vw,19px)', fontWeight: 400, color: DARK, margin: '0 0 8px', lineHeight: 1.25 }}>
          {post.title}
        </h3>
        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: MUTED, margin: 0, lineHeight: 1.65 }}>
          {post.lead}
        </p>
      </motion.article>
    </Link>
  );
}

/* ── Page ────────────────────────────────────────────────── */
export default function BlogArtykulPage() {
  usePrerenderReady();
  const params  = useParams<{ slug?: string; handle?: string }>();
  const [, nav] = useLocation();
  const topRef  = useRef<HTMLDivElement>(null);
  const [showTopBtn, setShowTopBtn] = useState(false);

  const articleSlug = params.slug ?? params.handle ?? '';
  const post = getPostBySlug(articleSlug);
  const seoTitle = post?.seo?.title ?? post?.title ?? 'Artykuł';
  const seoDescription = post?.seo?.description
    || post?.lead
    || `Przeczytaj artykuł „${seoTitle}” na Bukietowa.pl.`;
  const robots = getRobotsDirective(post?.seo);

  useSeo({
    title: seoTitle,
    description: seoDescription,
    robots,
    canonical: post?.seo?.canonical ?? undefined,
    ogImage: post?.image,
    ogType: 'article',
    noSuffix: Boolean(post?.seo?.title),
  });

  // Scroll to top on slug change
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [articleSlug]);

  // Show "back to top" after 400px scroll
  useEffect(() => {
    const onScroll = () => setShowTopBtn(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Same sanitize/style pipeline as before, now split into typed blocks
  // instead of one HTML blob — see blogContentBlocks.ts for why this
  // changes nothing about what gets rendered.
  const contentBlocks = useMemo(() => {
    if (!post) return [];
    const canonicalContent = post.content.replaceAll(
      'https://bukietowa.pl/flowerbox/',
      'https://bukietowa.pl/flower-boxy/',
    );
    return parseContentBlocks(DOMPurify.sanitize(styledContent(canonicalContent), { ADD_ATTR: ['style'] }));
  }, [post]);

  if (!post) {
    return (
      <div style={{ background: '#fff', minHeight: '100vh' }}>
        <PageBreadcrumb crumbs={[{ label: 'Blog', href: '/blog/' }, { label: 'Artykuł nie istnieje' }]} />
        <div style={{ maxWidth: 720, margin: '0 auto', padding: 'clamp(64px,8vw,120px) clamp(24px,5vw,48px)', textAlign: 'center' }}>
          <p style={{ fontFamily: 'DM Sans', fontSize: 9.5, letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD, marginBottom: 12 }}>404</p>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem,4vw,3.2rem)', fontWeight: 400, color: DARK, marginBottom: 16 }}>
            Artykuł nie istnieje
          </h1>
          <Link href="/blog/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, border: `1px solid rgba(42,31,20,0.25)`, padding: '12px 26px', textDecoration: 'none' }}>
            <ArrowLeft size={13} /> Wróć do Magazynu
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const related = getRelatedPosts(post, 3);

  /* JSON-LD Article */
  const absoluteImage = post.image?.startsWith('http')
    ? post.image
    : post.image
      ? `https://bukietowa.pl${post.image}`
      : undefined;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline:          post.title,
    description:       post.lead,
    author:            { '@type': 'Organization', name: post.author },
    datePublished:     post.publishedAt,
    publisher:         ARTICLE_PUBLISHER,
    image:             absoluteImage,
    keywords:          post.tags.join(', '),
    articleSection:    post.category,
     mainEntityOfPage:  { '@type': 'WebPage', '@id': `https://bukietowa.pl${articleUrl(post.slug)}` },
  };

  return (
    <div ref={topRef} style={{ background: '#fff', minHeight: '100vh' }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeCmsJson(jsonLd) }} />


      <PageBreadcrumb crumbs={[
        { label: 'Blog', href: '/blog/' },
        { label: post.title },
      ]} />

      {/* ── Article header ────────────────────────────── */}
      <header style={{ maxWidth: 800, margin: '0 auto', padding: 'max(clamp(48px,6vw,72px), calc(var(--page-breadcrumb-height, 44px) + 12px)) clamp(24px,5vw,48px) clamp(32px,4vw,48px)' }}>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease }}>
          {/* Back link */}
          <Link href="/blog/" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'DM Sans', fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: MUTED, textDecoration: 'none', marginBottom: 28 }}>
            <ArrowLeft size={12} /> Magazyn
          </Link>

          {/* Category */}
          <p style={{ fontFamily: 'DM Sans', fontSize: 9.5, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, margin: '0 0 16px' }}>
            {post.category}
          </p>

          {/* H1 */}
          <h1 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2.2rem,5vw,4rem)', fontWeight: 400, color: DARK, margin: '0 0 20px', lineHeight: 1.08, letterSpacing: '-0.01em' }}>
            {post.title}
          </h1>

          {/* Lead */}
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(15px,1.6vw,17px)', color: `rgba(42,31,20,0.65)`, lineHeight: 1.8, margin: '0 0 28px', fontStyle: 'italic' }}>
            {post.lead}
          </p>

          {/* Meta row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', paddingTop: 20, borderTop: `1px solid ${LINE}` }}>
            <span style={{ fontFamily: 'DM Sans', fontSize: 12, color: MUTED, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Calendar size={12} color={GOLD} /> {formatDate(post.publishedAt)}
            </span>
            <span style={{ fontFamily: 'DM Sans', fontSize: 12, color: MUTED, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={12} color={GOLD} /> {post.readTime} min czytania
            </span>
            <span style={{ fontFamily: 'DM Sans', fontSize: 12, color: MUTED }}>
              {post.author}
            </span>
          </div>
        </motion.div>
      </header>

      {/* ── Hero image ────────────────────────────────── */}
      {post.image && (
        <motion.div
          initial={{ opacity: 0, scale: 1.015 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease }}
          style={{ maxWidth: 1100, margin: '0 auto', padding: '0 clamp(24px,5vw,48px)', marginBottom: 'clamp(40px,5vw,64px)' }}
        >
          <div style={{ aspectRatio: '21/9', overflow: 'hidden', background: '#fff' }}>
            <img src={post.image} alt={post.imageAlt ?? post.title} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 30%', display: 'block' }} />
          </div>
          {post.imageAlt && (
            <p style={{ fontFamily: 'DM Sans', fontSize: 11.5, color: `${DARK}44`, margin: '8px 0 0', fontStyle: 'italic', textAlign: 'center' }}>
              {post.imageAlt}
            </p>
          )}
        </motion.div>
      )}

      {/* ── Article body ──────────────────────────────── */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 clamp(24px,5vw,48px) clamp(64px,8vw,96px)' }}>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.2 }}
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(15px,1.45vw,16.5px)', color: `rgba(42,31,20,0.80)`, lineHeight: 1.9 }}
        >
          <BlogContentBlocks blocks={contentBlocks} />
        </motion.div>

        {/* Tags */}
        {post.tags.length > 0 && (
          <motion.div {...fadeUp()} style={{ marginTop: 48, paddingTop: 28, borderTop: `1px solid ${LINE}`, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontFamily: 'DM Sans', fontSize: 10.5, color: MUTED, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Tagi:</span>
            {post.tags.map(tag => (
              <span key={tag} style={{ fontFamily: 'DM Sans', fontSize: 11, color: MUTED, border: `1px solid ${LINE}`, padding: '4px 10px' }}>
                #{tag}
              </span>
            ))}
          </motion.div>
        )}

        {/* Share / CTA */}
        <motion.div {...fadeUp()} style={{ marginTop: 48, background: '#fff', padding: 'clamp(24px,3vw,36px)', borderTop: `2px solid ${GOLD}`, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.3rem,2vw,1.7rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.3 }}>
            Potrzebujesz pomocy w wyborze kwiatów?
          </p>
          <p style={{ fontFamily: 'DM Sans', fontSize: 13.5, color: MUTED, margin: 0, lineHeight: 1.7 }}>
            Nasi florysci chętnie doradzą - zadzwoń lub napisz.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link href="/kontakt/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: DARK, padding: '12px 24px', textDecoration: 'none' }}>
              Skontaktuj się <ArrowRight size={12} />
            </Link>
            <Link href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, border: `1px solid rgba(42,31,20,0.2)`, padding: '12px 24px', textDecoration: 'none' }}>
              Zamów kwiaty
            </Link>
          </div>
        </motion.div>
      </div>

      {/* ── Related articles ──────────────────────────── */}
      {related.length > 0 && (
        <section style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0', borderTop: `1px solid ${LINE}` }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 clamp(24px,5vw,48px)' }}>
            <motion.div {...fadeUp()} style={{ marginBottom: 'clamp(28px,3.5vw,44px)' }}>
              <p style={{ fontFamily: 'DM Sans', fontSize: 9.5, letterSpacing: '0.3em', textTransform: 'uppercase', color: GOLD, margin: '0 0 8px' }}>
                Czytaj dalej
              </p>
              <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.8rem,3vw,2.6rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
                Może Cię zainteresować
              </h2>
            </motion.div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,280px),1fr))', gap: 'clamp(20px,3vw,36px)' }}>
              {related.map(p => <RelatedCard key={p.slug} post={p} />)}
            </div>
            <motion.div {...fadeUp(0.1)} style={{ marginTop: 40, textAlign: 'center' }}>
              <Link href="/blog/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, border: `1px solid rgba(42,31,20,0.2)`, padding: '13px 28px', textDecoration: 'none' }}>
                <ArrowLeft size={13} /> Wszystkie artykuły
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      <Footer />

      {/* ── Floating back-to-top ──────────────────────── */}
      <AnimatePresence>
        {showTopBtn && (
          <motion.button
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.25 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Wróć na górę"
            style={{
              position: 'fixed', bottom: 28, right: 28, zIndex: 50,
              width: 44, height: 44, borderRadius: '50%', background: DARK,
              border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(42,31,20,0.25)',
            }}
          >
            <ArrowUp size={16} color="#fff" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Content styling helper ─────────────────────────────── */
function getRobotsDirective(seo?: PostSeo): string | undefined {
  const noindex = seo?.noindex === true;
  const nofollow = seo?.nofollow === true;
  if (!noindex && !nofollow) return undefined;
  return `${noindex ? 'noindex' : 'index'},${nofollow ? 'nofollow' : 'follow'}`;
}

function styledContent(html: string): string {
  // Inject inline styles into common elements
  return html
    .replace(/<h2>/g,  `<h2 style="font-family:'Cormorant Garamond',Georgia,serif;font-size:clamp(1.5rem,2.5vw,2rem);font-weight:400;color:#2a1f14;margin:2em 0 0.6em;line-height:1.15;border-bottom:1px solid rgba(210,60,120,0.18);padding-bottom:0.4em">`)
    .replace(/<h3>/g,  `<h3 style="font-family:'Cormorant Garamond',Georgia,serif;font-size:clamp(1.2rem,2vw,1.5rem);font-weight:400;color:#2a1f14;margin:1.6em 0 0.5em;line-height:1.2">`)
    .replace(/<p>/g,   `<p style="margin:0 0 1.4em;font-family:'DM Sans',sans-serif;font-size:clamp(15px,1.45vw,16.5px);color:rgba(42,31,20,0.80);line-height:1.9">`)
    .replace(/<ul>/g,  `<ul style="margin:0 0 1.4em;padding-left:1.5em;color:rgba(42,31,20,0.75);line-height:1.9;font-family:'DM Sans',sans-serif;font-size:clamp(15px,1.45vw,16.5px)">`)
    .replace(/<ol>/g,  `<ol style="margin:0 0 1.4em;padding-left:1.5em;color:rgba(42,31,20,0.75);line-height:1.9;font-family:'DM Sans',sans-serif;font-size:clamp(15px,1.45vw,16.5px)">`)
    .replace(/<li>/g,  `<li style="margin-bottom:0.4em">`)
    .replace(/<strong>/g, `<strong style="font-weight:600;color:#2a1f14">`);
}
